import { DndContext, DragEndEvent } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { Button } from "../../../components/buttons/button";
import { EmptyState } from "../../../components/empty";
import { Icon } from "../../../components/icon/icon";
import { EventTicketModal } from "../../../components/modal/app_modals/eventTicketModal";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import { useModalControl } from "../../../customeHooks/useModalControl";
import {
  CreateEvent,
  CreateTicket,
} from "../../../redux/features/event/event.types";
import { FEE_PERCENTAGE_TEXT } from "../../../utils/constants";
import {
  deepCopy,
  formatToNaira,
  moveElement,
  removeElementAtIndex,
} from "../../../utils/helpers";

export const Ticket = ({
  ticket,
  handleModalOpen,
  setSelectedTicket,
  setSelectedTicketIdx,
  values,
  setFieldValue,
  idx,
}: {
  ticket: { id: string } & CreateTicket;
  handleModalOpen: (modal: string) => void;
  setSelectedTicket: React.Dispatch<
    React.SetStateAction<CreateTicket | undefined>
  >;
  setSelectedTicketIdx: React.Dispatch<
    React.SetStateAction<number | undefined>
  >;
  values: { tickets: CreateTicket[] };
  setFieldValue: (field: string, value: any) => void;
  idx: number;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: ticket.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <BgWrapper className="flex items-center gap-4">
        <button {...attributes} {...listeners} className="w-fit" type="button">
          <Icon svg="grip" width={20} height={20} className="cursor-grab" />
        </button>

        <div className="flex-1 text-sm text-grey-900">
          <p className="text-base font-bold">{ticket.name}</p>
          <p className="capitalize">
            {ticket.ticketType === "free"
              ? "Free"
              : formatToNaira(ticket.price!)}
          </p>
          <p className="capitalize">
            {ticket.quantityType === "unlimited"
              ? "Unlimited"
              : `0/${ticket.quantityAvailable}`}
          </p>
        </div>

        <button
          className=""
          type="button"
          onClick={() => {
            setSelectedTicketIdx(idx);
            setSelectedTicket(ticket);
            handleModalOpen("eventTicketModal");
          }}
        >
          <Icon svg="pencil" width={20} height={20} />
        </button>

        <button
          className=""
          type="button"
          onClick={() => {
            setFieldValue("tickets", removeElementAtIndex(values.tickets, idx));
          }}
        >
          <Icon svg="bin" width={20} height={20} />
        </button>
      </BgWrapper>
    </div>
  );
};

export const EventTickets = ({
  finalPayload,
  setFinalPayload,
  setStep,
}: {
  finalPayload: CreateEvent;
  setFinalPayload: React.Dispatch<React.SetStateAction<CreateEvent>>;
  setStep: (step: number) => void;
}) => {
  const [selectedTicket, setSelectedTicket] = useState<CreateTicket>();
  const [selectedTicketIdx, setSelectedTicketIdx] = useState<number>();

  const { modal, modalOpen, handleModalOpen, handleModalClose } =
    useModalControl();

  const { values, handleSubmit, setFieldValue } = useFormik({
    initialValues: {
      tickets: finalPayload.tickets,
    } as { tickets: CreateTicket[] },
    validationSchema: undefined,
    onSubmit: async (values) => {
      setStep(3);
    },
  });

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;

    if (over && active.id !== over.id) {
      const ticketsCopy: CreateTicket[] = deepCopy(values.tickets);
      const ticketCopyMod = ticketsCopy.map((t) => {
        return {
          id: `${t.name}-${t.price}`,
          ...t,
        };
      });

      const oldIndex = ticketCopyMod.findIndex((item) => item.id === active.id);
      const newIndex = ticketCopyMod.findIndex((item) => item.id === over.id);

      setFieldValue("tickets", moveElement(ticketsCopy, oldIndex, newIndex));
    }
  };

  useEffect(() => {
    setFinalPayload({
      ...finalPayload,
      tickets: JSON.parse(JSON.stringify(values.tickets)),
    });
    // eslint-disable-next-line
  }, [values.tickets]);
  return (
    <>
      <div className="flex w-full flex-col gap-[2px]">
        <BgWrapper className="flex flex-col items-start gap-[2px] text-grey-950 md:flex-row md:gap-10">
          <div className="flex-1">
            <p className="text-base font-bold">Create Tickets</p>
            {values.tickets.length > 0 && (
              <p className="text-sm text-grey-500">
                We don't charge fees for free tickets. For paid tickets, we
                charge a {FEE_PERCENTAGE_TEXT} per ticket and this fee is passed
                to the attendees by default.
              </p>
            )}
          </div>

          <button
            type="button"
            className="min-w-fit text-sm font-bold underline md:text-base"
            onClick={() => {
              handleModalOpen("eventTicketModal");
            }}
          >
            Add New
          </button>
        </BgWrapper>

        {values.tickets.length < 1 && (
          <BgWrapper className="flex flex-col gap-[2px]">
            <EmptyState
              text="No Tickets Created"
              subtext={`Create paid or free tickets for your event. Click on "Create Ticket" to get started.`}
            />
          </BgWrapper>
        )}

        {values.tickets.length > 0 && (
          <DndContext
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext
              items={values.tickets.map((t) => {
                return {
                  id: `${t.name}-${t.price}`,
                  ...t,
                };
              })}
            >
              {values.tickets
                .map((t) => {
                  return {
                    id: `${t.name}-${t.price}`,
                    ...t,
                  };
                })
                .map((ticket, idx) => {
                  return (
                    <Ticket
                      key={ticket.id}
                      ticket={ticket}
                      handleModalOpen={handleModalOpen}
                      setSelectedTicket={setSelectedTicket}
                      setSelectedTicketIdx={setSelectedTicketIdx}
                      values={values}
                      setFieldValue={setFieldValue}
                      idx={idx}
                    />
                  );
                })}
            </SortableContext>
          </DndContext>
        )}

        <BgWrapper className="grid grid-cols-2 items-center gap-2.5">
          <Button
            text="Back"
            type="button"
            onClick={() => {
              setStep(1);
            }}
            variation="plain"
            className=""
          />
          <Button
            text={values.tickets.length > 0 ? "Next" : "Create Ticket"}
            type="button"
            onClick={() => {
              if (values.tickets.length > 0) {
                handleSubmit();
              } else {
                handleModalOpen("eventTicketModal");
              }
            }}
          />
        </BgWrapper>
      </div>

      <EventTicketModal
        open={modalOpen && modal === "eventTicketModal"}
        handleClose={() => {
          setSelectedTicket(undefined);
          setSelectedTicketIdx(undefined);
          handleModalClose();
        }}
        ticket={selectedTicket}
        selectedIdx={selectedTicketIdx}
        tickets={values.tickets}
        onSuccess={(tickets) => {
          setFieldValue("tickets", tickets);
        }}
      />
    </>
  );
};
